/* https://brandplatform.smartphoto.com/general/atoms/amount/ */
import {
	expressAddClass,
	expressEventListener,
	expressQuerySelector,
	expressRemoveClass,
	hasClass
} from "../../common/html";
import {compositeDisposable} from "../../utils/disposable";
import {onlyNumber} from "../../utils/validation";

export interface IAmountOptions {
	readonly onSubtractClick?: (amount: number) => void;
	readonly onAddClick?: (amount: number) => void;
	readonly onManualInput?: (amount: number) => void;
}

export interface IAmount {
	readonly dispose: () => void;
	readonly getCurrentValue: () => string;
	readonly getCode: () => string;
	readonly setValue: (value: number) => void;
	readonly minAmount: number;
	readonly maxAmount: number;
}

export function createAmount(container: HTMLElement, opts: IAmountOptions)
{
	const subtractEl = expressQuerySelector<HTMLElement>(container, '.technical-subtract', true);
	const addEl = expressQuerySelector<HTMLElement>(container, '.technical-add', true);
	const amountValueEl = expressQuerySelector<HTMLInputElement>(container, '.technical-amountvalue', true);
	const minAmountFormAttribute = parseInt(amountValueEl.dataset.minamount || "1", 10);
	const minAmount = !minAmountFormAttribute && minAmountFormAttribute != 0 ? 1 : minAmountFormAttribute;
	const maxAmount = parseInt(amountValueEl.dataset.maxamount || "999", 10) || 999;
	let currentValue = amountValueEl.value;
	const regex = new RegExp("[^0-9]", 'g');

	const {
		onSubtractClick,
		onAddClick,
		onManualInput
	} = opts;

	const onSubtractClickEvent = (ev: MouseEvent) => {
		ev.preventDefault();
		const currentAmount = parseInt(amountValueEl.value, 10) - 1;
		if (hasClass(subtractEl, 'a-button--disabled') ||isNaN(currentAmount) || currentAmount < minAmount) return;

		currentValue = currentAmount+'';
		amountValueEl.value = currentAmount+'';
		onSubtractClick && onSubtractClick(currentAmount);
		updateDisabledButtons(currentAmount);
	};

	const onAddClickEvent = (ev: MouseEvent) => {
		ev.preventDefault();
		const currentAmount = parseInt(amountValueEl.value, 10) + 1;
		if (hasClass(addEl, 'a-button--disabled') || isNaN(currentAmount) || currentAmount >maxAmount) return;

		currentValue = currentAmount+'';
		amountValueEl.value = currentAmount+'';
		onAddClick && onAddClick(currentAmount);
		updateDisabledButtons(currentAmount);
	};

	const updateDisabledButtons = (amount: number) => {
		if (amount < maxAmount)
			expressRemoveClass(addEl, 'a-button--disabled');
		else
			expressAddClass(addEl, 'a-button--disabled');

		if (amount > minAmount)
			expressRemoveClass(subtractEl, 'a-button--disabled');
		else
			expressAddClass(subtractEl, 'a-button--disabled');
	};

	const onInput = () => {
		if (!onlyNumber(amountValueEl.value)) {
			amountValueEl.value = '';
			return;
		}
		const inputValue = parseInt(amountValueEl.value, 10);
		if (isNaN(inputValue) || inputValue < minAmount || inputValue > maxAmount) {
			amountValueEl.value = '';
			return;
		}
		currentValue = inputValue+'';
		updateDisabledButtons(inputValue);
		if (onManualInput) onManualInput(inputValue);
	};

	const onFocusOut = () => {
		const currentAmount = parseInt(amountValueEl.value, 10);
		if (amountValueEl.value.match(regex) || isNaN(currentAmount) || currentAmount > maxAmount || currentAmount < minAmount) {
			amountValueEl.value = currentValue;
		}
	};

	const disposable = compositeDisposable([
		expressEventListener(addEl, 'click', onAddClickEvent),
		expressEventListener(subtractEl, 'click', onSubtractClickEvent),
		expressEventListener(amountValueEl, 'input', onInput),
		expressEventListener(amountValueEl, 'focusout', onFocusOut)
	]);

	const getCurrentValue = () => {
		return amountValueEl.value;
	};

	const getCode = () => {
		return amountValueEl.dataset.attributecode || '';
	};

	return {
		dispose: () => disposable.dispose(),
		getCurrentValue,
		getCode,
		setValue: (value: number) => { amountValueEl.value = `${value}`; updateDisabledButtons(value); },
		minAmount,
		maxAmount
	} as IAmount;
}
